<template>
  <PolicyLinksManager
    ref="managerRef"
    :resource-type="props.resourceType"
    :resource-id="props.resourceId"
    :company-id="props.companyId"
    :adapter="adapter"
    bypass-edit-permission
    @update:is-saving="emit('update:isSaving', $event)"
    @update:has-unsaved-changes="emit('update:hasUnsavedChanges', $event)"
  />
</template>

<script lang="ts" setup>
import { ref } from 'vue'

import Network from '@/Network'
import PolicyLinksManager from '@/components/designSystem/PolicyLinksManager/index.vue'
import type { PolicyLinksAdapter } from '@/components/designSystem/PolicyLinksManager/types'
import type {
  ListQualificationTransferPolicyLinksResponse,
  QualificationTransferPolicyLinkItem
} from '@/Network/Types/Responses/QualificationTransferResponses'
import type { UpdateQualificationTransferPolicyLinksRequest } from '@/Network/Types/Requests/QualificationTransferRequests'
import { AuthorizationResourcesAndSubjects } from '@/types/authorization'

const props = defineProps<{
  resourceType: AuthorizationResourcesAndSubjects
  resourceId: number
  companyId: number
}>()

const emit = defineEmits<{
  'update:isSaving': [value: boolean]
  'update:hasUnsavedChanges': [value: boolean]
}>()

const service = Network.qualificationTransferPolicy

const listByType: Partial<
  Record<
    AuthorizationResourcesAndSubjects,
    (id: number) => Promise<ListQualificationTransferPolicyLinksResponse>
  >
> = {
  [AuthorizationResourcesAndSubjects.Company]: (id) => service.listCompanyPolicyLinks(id),
  [AuthorizationResourcesAndSubjects.HealthUnit]: (id) => service.listHealthUnitPolicyLinks(id),
  [AuthorizationResourcesAndSubjects.Position]: (id) => service.listPositionPolicyLinks(id)
}

const updateByType: Partial<
  Record<
    AuthorizationResourcesAndSubjects,
    (id: number, payload: UpdateQualificationTransferPolicyLinksRequest) => Promise<void>
  >
> = {
  [AuthorizationResourcesAndSubjects.Company]: (id, payload) =>
    service.updateCompanyPolicyLinks(id, payload),
  [AuthorizationResourcesAndSubjects.HealthUnit]: (id, payload) =>
    service.updateHealthUnitPolicyLinks(id, payload),
  [AuthorizationResourcesAndSubjects.Position]: (id, payload) =>
    service.updatePositionPolicyLinks(id, payload)
}

// endDate null = vigência aberta (permitida pelo backend só na última política).
// A UI atual não edita vigência aberta: o item renderiza sem data final e o save
// exige preenchê-la — suportar edição de vigência aberta é follow-up de produto.
const toListItem = (item: QualificationTransferPolicyLinkItem) => ({
  policyId: item.qualificationTransferPolicy.id,
  policyName: item.qualificationTransferPolicy.name,
  startDate: item.startDate,
  endDate: item.endDate ?? '',
  linkedAt: item.createdAt,
  linkedBy: item.linkedBy?.name ?? ''
})

const adapter: PolicyLinksAdapter = {
  policyColumnLabel: 'Política de repasse',
  listLinks: async (resourceType, resourceId) => {
    const list = listByType[resourceType]
    if (!list) throw new Error('Tipo de recurso não suportado para políticas de repasse.')
    const response = await list(resourceId)
    return response.data.map(toListItem)
  },
  saveLinks: async (resourceType, resourceId, items) => {
    const update = updateByType[resourceType]
    if (!update) throw new Error('Tipo de recurso não suportado para políticas de repasse.')
    await update(resourceId, { policies: items })
  },
  fetchPolicyOptions: async (params) => {
    const response = await service.listPolicyOptions(params)
    return response.data
  }
}

const managerRef = ref<InstanceType<typeof PolicyLinksManager> | null>(null)

const handleSave = async () => {
  await managerRef.value?.handleSave()
}

defineExpose({ handleSave })
</script>
